import theme from "../../../../theme";

/**
 * Styles for the Specifications component
 * @module SpecificationsSx
 * @description Contains all style objects for the Specifications component with exact visual matching
 */

/**
 * Root container styles
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} width - Full width (100%)
 */
export const rootContainer = {
  display: "flex",
  width: "100%"
};

/**
 * Main content container styles
 * @type {Object}
 * @property {string} flex - Flex grow
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} background - Background color from theme
 * @property {string} margin - Responsive margin
 */
export const contentContainer = {
  flex: "1",
  display: "flex",
  flexDirection: "column",
  background: theme.palette.background.paper,
  margin: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem"
  }
};

/**
 * Form container styles
 * @type {Object}
 * @property {string} flex - Flex grow
 * @property {string} padding - Responsive padding
 * @property {string} maxWidth - No max width restriction
 */
export const formContainer = {
  flex: 1,
  padding: {
    xs: "1rem",
    sm: "1.5rem",
    md: "2rem"
  },
  maxWidth: "none !important"
};

/**
 * Form element styles
 * @type {Object}
 * @property {string} height - Full height
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} justifyContent - Space between content
 */
export const formStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between"
};

/**
 * Section container styles
 * @type {Object}
 * @property {string} mb - Responsive bottom margin
 */
export const sectionContainer = {
  mb: {
    xs: "1.5rem",
    sm: "1.75rem",
    md: "2rem"
  }
};

/**
 * Standard text styles
 * @type {Object}
 * @property {string} mb - Responsive bottom margin
 * @property {string} fontSize - Responsive font size
 * @property {string} color - Text primary color from theme
 */
export const standardText = {
  mb: {
    xs: "0.5rem",
    sm: "0.375rem",
    md: "0.25rem"
  },
  fontSize: {
    xs: "0.95rem",
    sm: "1rem"
  },
  color: theme.palette.text.primary
};

/**
 * Checkbox container styles
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} gap - 0.5rem gap between items
 * @property {string} flexWrap - Wrap content
 */
export const checkboxContainer = {
  display: "flex",
  gap: "0.5rem",
  flexWrap: "wrap"
};

/**
 * Grid container styles
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} gap - 0.5rem gap between items
 * @property {string} flexWrap - Wrap content
 */
export const gridContainer = {
  display: "flex",
  gap: "0.5rem",
  flexWrap: "wrap"
};

/**
 * Heading text styles
 * @type {Object}
 * @property {string} color - Text primary color from theme
 * @property {string} fontWeight - Bold font weight
 * @property {string} fontSize - Responsive font size
 */
export const headingText = {
  color: theme.palette.text.primary,
  fontWeight: "bold",
  fontSize: {
    xs: "1.1rem",
    sm: "1.18rem",
    md: "1.24rem"
  }
};

/**
 * Subtitle text styles
 * @type {Object}
 * @property {string} mb - Responsive bottom margin
 * @property {string} fontSize - Responsive font size
 */
export const subtitleText = {
  mb: {
    xs: "0.75rem",
    sm: "0.625rem",
    md: "0.5rem"
  },
  fontSize: {
    xs: "0.95rem",
    sm: "1rem"
  }
};

/**
 * Text field styles
 * @type {Object}
 * @property {string} width - Full width
 * @property {string} multiline - Enabled
 * @property {number} rows - 4 rows
 */
export const textFieldStyles = {
  width: "100%",
};